<template>
  <div class="contacts-table__outer">
    <div v-show="_isLoading" id="loadign-backdrop">
      <WithLoader :isLoading="_isLoading" classType="primary"> </WithLoader>
    </div>
    <div
      class="contacts-table__wrapper"
      :class="{ [extraClass]: !!extraClass }"
    >
      <VDataTable
        :extraClass="
          `contacts-table--extra ${
            contacts && contacts.length > menuSwitchingCount ? 'large' : ''
          }`
        "
        classType="primary"
        :headers="tableFields.headers"
        :rows="tableFields.rows"
        :currentSort="{ ...sort, value: !!sort.order }"
        tableType="text"
        @sort="onSort"
      >
      </VDataTable>
    </div>
    <VConfirmModal
      :isOpened="deleteContactConfirmOpened"
      @approve="_deleteContact"
      @cancel="deleteContactConfirmOpened = false"
      @close="deleteContactConfirmOpened = false"
    >
      <div class="confirm-message">
        Deleting the
        <span>
          {{ contactToDelete.name ? contactToDelete.name : "Noname" }}
        </span>
        contact?
      </div>
    </VConfirmModal>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapActions, mapGetters } from "vuex";
import { debounce } from "lodash";

// components
import VDataTable from "@/components/VDataTable";
import WithLoader from "@/components/WithLoader";
import VConfirmModal from "@/components/VConfirmModal";
import VTextTooltiped from "@/components/VTextTooltiped";
import VDateTooltiped from "@/components/VDateTooltiped";
import VTagsExpandableList from "@/components/VTagsExpandableList";
import FilterByTags from "@/components/FilterByTags";

// mixins
import withToasted from "@/mixins/withToasted";
import withNavigate from "@/mixins/withNavigate";
import withCopy from "@/mixins/withCopy";

import mapKeys from "lodash/mapKeys";

import { TABLE_SWITCHING_CONTEXT_MENU_MIN_COUNT as menuSwitchingCount } from "@/constants/common";
import csvHistoryLinkIcon from "@/assets/images/history-link-icon.svg";
import csvHistoryLinkIconActive from "@/assets/images/history-link-icon-active.svg";
import { getTableContextMenu } from "@/utils/table";
import {
  generateUUID,
  getGravatarUrl,
  convertTagsToSelectListFormat,
  insertOrDeleteByKey
} from "@/utils/util";

const ImportHistoryIcon = Vue.component("ImportHistoryIcon", {
  props: ["contactsLength"],

  mixins: [withNavigate],

  data() {
    return {
      historyIcon: csvHistoryLinkIcon,
      csvHistoryLinkIcon,
      csvHistoryLinkIconActive
    };
  },

  template: `
        <div
        v-if="contactsLength > 0"
        class="csv-history-link"
        @mouseover="historyIcon = csvHistoryLinkIconActive"
        @mouseleave="historyIcon = csvHistoryLinkIcon"
        @click="navigate('/contacts/import-history')"
        title="Import history"
      >
        <img :src="historyIcon" alt="link" />
      </div>
  `
});

const VAvatarWithName = Vue.component("VAvatarWName", {
  props: ["firstName", "lastName", "email"],

  data() {
    return {
      isActive: false
    };
  },

  computed: {
    _gravatarImgLink() {
      return getGravatarUrl(this.email);
    }
  },

  template: `
      <div class="v-avatar-name" @mouseenter="isActive = true" @mouseleave="isActive = false">
          <VAvatar
            height="34px"
            width="34px"
            fontSize="12px"
            :firstName="firstName"
            :lastName="lastName"
            :isActive="isActive"
            useColorFromPalette
            ></VAvatar>

            <span class="name">
            {{firstName}} {{lastName}}
           </span>
      </div>
  `
});

export default Vue.component("ContactsTable", {
  props: {
    extraClass: VueTypes.string.def("")
  },

  mixins: [withNavigate, withToasted, withCopy],

  components: {
    VDataTable,
    WithLoader,
    VConfirmModal,
    VTextTooltiped,
    FilterByTags
  },

  data() {
    return {
      deleteContactConfirmOpened: false,
      contactToDelete: {},
      expandedIds: [],
      menuSwitchingCount,
      tags: [],
      selectedTags: [],
      //helper for sending tags
      //to inner components
      convertedTags: [],
      tagsFilterOpened: false
    };
  },

  methods: {
    getTagsString(tags) {
      if (tags && tags.length) {
        return tags
          .map(tagI => {
            const tag = this.getTag(tagI);

            if (tag && tag.name) {
              return tag.name;
            }
          })
          .join(", ");
      }

      return "No tags";
    },

    _deleteContact() {
      this.deleteContact({
        id: this.contactToDelete.id,
        onSuccess: () => {
          this.toast("Contact has been deleted successfully.");
          this.deleteContactConfirmOpened = false;
        },

        onError: () => {
          this.toast(
            "Failed to delete contact. Please, try again later.",
            "error"
          );
        }
      });
    },

    handleDeleteContact(e, id, name) {
      e.stopPropagation();
      this.deleteContactConfirmOpened = true;
      this.contactToDelete = {
        id,
        name
      };
    },

    onSort(sort) {
      this.fetchContactList({
        sort,
        page: this.pagination.current_page,
        search: this.search,
        page_size: this.itemsPerPage,
        queryString: this.selectedTags.length ? `&data-tags=${this.selectedTags}`: ""
      });

      this.changeSort(sort);
    },

    onTagsFilter: debounce(function(tags) {
      let tagsIds = tags.map(tag => tag.id).join(",");
      this.selectedTags = tagsIds;

      this.fetchContactList({
        page: 1,
        sort: this.sort,
        page_size: this.itemsPerPage,
        queryString: tagsIds ? `&data-tags=${tagsIds}` : ""
      });
    }, 400),

    ...mapActions("contacts", [
      "fetchContactList",
      "changeSort",
      "deleteContact"
    ]),
    ...mapActions("tags", ["fetchTagsPerPageList"])
  },

  computed: {
    tableFields() {
      return {
        headers: [
          {
            key: "name",
            label: "Name",
            sortable: true,
            extraClass: "name",
            sortType: "string",
            width: "16%"
          },
          {
            key: "updated_at",
            label: "Modified",
            sortable: true,
            extraClass: "updated-at",
            width: "14%"
          },
          {
            key: "address",
            label: "Address",
            extraClass: "address",
            width: "20%"
          },
          {
            key: "city_state",
            label: "City, State",
            extraClass: "city-state",
            sortable: true,
            sortType: "string",
            width: "15%"
          },
          {
            key: "address_country",
            label: "Country",
            sortable: true,
            sortType: "string",
            extraClass: "country",
            width: "8%"
          },
          {
            key: "tags",
            label: "Tags",
            extraClass: this.tagsFilterOpened && "tags-active",
            width: "7%",
            component: FilterByTags,
            props: {
              extraClass: "in-contacts-table",
              onStatusUpdate: status => (this.tagsFilterOpened = status),
              isOpened: this.tagsFilterOpened
            },
            display: true,
            extraClass: "tags-header"
          },
          {
            key: "actions",
            label: "",
            display: true,
            width: "3%",
            component: ImportHistoryIcon,
            props: {
              contactsLength: this.contacts.length
            },
            extraClass: "import-history"
          }
        ],

        rows: this.contacts.map(contact => ({
          key: contact.id,
          click: () => {
            this.navigate({
              name: "contact-details",
              params: { id: contact.id }
            });
          },
          values: [
            {
              key: "name-preview",
              component: VAvatarWithName,
              props: {
                firstName: contact.first_name,
                lastName: contact.last_name,
                email: contact.email
              },
              extraClass: "name-preview"
            },
            {
              key: "updated_at",
              component: VDateTooltiped,
              props: {
                date: contact.updated_at
              },
              extraClass: "updated-at"
            },

            {
              key: "address",
              value: contact.address_line_1,
              extraClass: "address"
            },
            {
              key: "city",
              value: `${contact.address_city}${
                contact.address_state ? "," : ""
              } ${contact.address_state &&
                contact.address_state.toUpperCase()}`,
              extraClass: `city-state`
            },
            {
              key: "country",
              value:
                contact.address_country &&
                contact.address_country.toUpperCase(),
              extraClass: "country"
            },
            {
              key: "tags",
              value: this.getTagsString(contact.tags),
              component: VTagsExpandableList,
              props: {
                tags: contact.tags.reduce((arr, tag) => {
                  if (this.convertedTags[tag]) {
                    arr.push(this.convertedTags[tag]);
                  }
                  return arr;
                }, []),
                onExpand: id => {
                  this.expandedIds = insertOrDeleteByKey(id, this.expandedIds);
                },
                identifier: contact.id
              },
              extraClass: "tags"
            },

            getTableContextMenu({
              copy: (e, a) => this.copy(contact.id),
              remove: e =>
                this.handleDeleteContact(
                  e,
                  contact.id,
                  `${contact.first_name} ${contact.last_name}`
                )
            })
          ],
          extraClass: this.expandedIds.includes(contact.id) ? "tags-expand" : ""
        }))
      };
    },

    selectedTagsStore() {
      return this.tagsStore("selectedTags");
    },

    _isLoading() {
      return this.isLoading || this.isTagsPerPageLoading;
    },

    ...mapGetters("contacts", [
      "isLoading",
      "contacts",
      "pagination",
      "search",
      "sort",
      "getTag",
      "identityLoading"
    ]),

    ...mapGetters("service", ["itemsPerPage"]),
    ...mapGetters("tags", {
      tagsStore: "tags",
      isTagsPerPageLoading: "isTagsPerPageLoading"
    })
  },

  watch: {
    contacts(val) {
      if (val) {
        const uniqueTags = [
          ...new Set(val.map(contact => contact.tags).flat())
        ];
        const uniqueTagsString = `&id=${[...uniqueTags].join(",")}`;
        this.fetchTagsPerPageList({
          queryString: uniqueTagsString,
          onSuccess: resp => {
            const convertedTags = convertTagsToSelectListFormat(resp);
            this.convertedTags = mapKeys(convertedTags, "value");
          }
        });
      }
    },

    selectedTagsStore(tags) {
      this.onTagsFilter(tags);
    }
  },

  async created() {
    await this.fetchContactList({
      page: 1,
      sort: this.sort,
      search: this.search,
      page_size: this.itemsPerPage
    });
  },

  destroyed() {
    this.changeSort({ key: "updated_at", order: 1 });
  }
});
</script>

<style lang="scss" scoped>
.contacts-table__outer {
  height: 100%;

  ::v-deep #filter-by-tags {
    margin-left: 13px;
    .filter-by-tags__actions {
      right: 0;
      top: 35px;
    }
  }
  ::v-deep .tags-list__wrapper {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  ::v-deep .tags-header {
    padding-left: 7px;
    z-index: 7;
  }
}
#loadign-backdrop {
  background: rgba(255, 255, 255, 0.3);
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 7;
}
</style>

<style lang="scss">
$ns: "contacts-table";

.#{$ns} {
  &__wrapper {
    .csv-history-link {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      font-size: 15px;
      font-family: "Lato-Bold", sans-serif;
      cursor: pointer;

      span {
        display: inline-block;
        margin-right: 13px;
        color: $primary-color;
      }
    }

    .table-row {
      cursor: pointer;
    }

    .#{$ns}--extra {
      th,
      td {
        &.country {
          /*text-align: center;*/
        }

        &.tags {
          padding: 5px 10px;

          &.expanded {
            vertical-align: top;
            padding-top: 10px;
          }
        }

        &.address {
          @include textCropEllipsis();
          max-width: 120px;
          padding-right: 20px;
        }

        &.name-preview {
          width: 1%;
          padding-right: 30px;
        }

        &:last-child {
          padding: 0;

          &.contacts-vlink-menu {
            width: 2%;

            /deep/ .v-context-menu__wrapper.round {
              justify-content: flex-end;
            }
          }
        }
      }

      th {
        &.tags-active {
          z-index: 7;
          color: $primary-color;
        }

        &.import-history {
          padding-right: 5px;
          z-index: 1;
        }
      }

      tr {
        td {
          &.name {
            white-space: normal;

            span {
              max-width: 170px;
              width: 170px;
            }
          }

          &.city-state {
            padding-right: 35px;
          }

          &.clamped {
            span {
              line-height: 17px;
              @include clamp(34px);
            }
          }
        }

        &.tags-expand {
          td {
            padding-top: 25px;
            vertical-align: top;

            &.tags {
              padding-top: 5px;
            }

            &.name-preview {
              padding-top: 15px;
            }
          }
        }
      }
    }

    /deep/ .expand {
      margin-top: 0;
    }
  }

  &__no-data {
    padding-bottom: 20px;
  }
}

table.large {
  tr {
    @include quantity-query($menu-switching-last-items-count, less) {
      td.contacts-vlink-menu {
        .menu-wrapper {
          bottom: 35px;
          top: auto;
        }
      }
    }
  }
}

.v-avatar-name {
  display: flex;
  align-items: center;

  .name {
    margin-left: 15px;
    max-width: 170px;
    width: 170px;
    line-height: 1.2em;

    @include textCropEllipsis();
  }

  &:hover .name {
    color: $primary-color;
  }
}
</style>
